import { TraitCode } from '@capital-markets-gateway/api-client-identity';
import { useCheckAccountTraits } from '@cmg/auth';
import { Box, Button, Divider, KebabMenu, ListItemText, MenuItem, Stack } from '@cmg/design-system';
import pluralize from 'pluralize';
import React, { useContext } from 'react';

import { OptInFeatureToggle } from '../../../../common/components/buttons/opt-in-toggle/OptInFeatureToggle';
import { USER_PREFERENCE_PREFERENCE_KEYS } from '../../../shared/constants/user-preferences';
import ScreenHeader from '../../../shared/layout/ScreenHeader';
import { CALENDAR_NEXT_MIXPANEL_EVENT_NAME } from '../../CalendarRoute.model';
import { ExportCalendarGridContext } from '../../context/ExportCalendarGridContext';
import { CalendarTabs } from '../calendar-tabs/CalendarTabs';
import type { TabValue } from '../calendar-tabs/CalendarTabs.model';
import { ExportButton } from './ExportButton';

export type CalendarHeaderProps = {
  activeTab: TabValue;
  setActiveTab: (value: TabValue) => void;
  alert?: React.ReactNode;
  activeFilterCount: number;
  onResetFilters: () => void;
  onResetGridToDefault: () => void;
};

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  alert,
  activeTab,
  setActiveTab,
  activeFilterCount,
  onResetFilters,
  onResetGridToDefault,
}) => {
  const hasExportTrait = useCheckAccountTraits([TraitCode.DL_EXCEL_DOWNLOAD]);
  const { handleExport, isExporting } = useContext(ExportCalendarGridContext);

  return (
    <Box>
      <ScreenHeader />
      {alert && <Box px={2}>{alert}</Box>}
      <Stack flexDirection="row" px={2}>
        <CalendarTabs activeTab={activeTab} onChange={setActiveTab} />
        <Stack flexDirection="row" flex={1} alignItems="center" justifyContent="flex-end" gap={2}>
          {activeFilterCount > 0 && (
            <Button variant="outlined" onClick={onResetFilters}>
              Reset {pluralize('Filter', activeFilterCount, true)}
            </Button>
          )}

          <OptInFeatureToggle
            mixpanelEventName={CALENDAR_NEXT_MIXPANEL_EVENT_NAME}
            preferenceKey={USER_PREFERENCE_PREFERENCE_KEYS.CalendarRewriteOptIn}
          />

          {hasExportTrait && <ExportButton loading={isExporting} onExport={handleExport} />}

          <KebabMenu slotProps={{ button: { 'aria-label': 'Calendar menu' } }}>
            <MenuItem dense onClick={onResetGridToDefault}>
              <ListItemText>Reset Calendar to Default</ListItemText>
            </MenuItem>
          </KebabMenu>
        </Stack>
      </Stack>
      <Divider />
    </Box>
  );
};
